import Image from 'next/image';
import cn from 'classnames';
import {
  MegaMenuLevels,
  trackMegaMenuNavigation,
  isModifiedClick,
  MENU_PATH_DELIMITER,
} from '_utils/GA4/trackMegaMenuEvents';
import styles from '../MegaMenu.module.scss';
import type { MenuItem } from '../types';
import { FeatureCard } from '../FeatureCard';
import { LinkColumns } from '../LinkColumns';

export interface MenuContentProps {
  item: MenuItem;
}

export function MenuContent({ item }: MenuContentProps) {
  if (!item.linkColumns) return null;

  const isWide = item.featureCard?.cardSize === 'Wide';
  const isSideBySide = item.featureCard?.layoutStyle === 'Side by side';
  const menuPathPrefix = item.title?.label
    ? `${item.label}${MENU_PATH_DELIMITER}${item.title.label}`
    : item.label;

  return (
    <>
      {item.featureCard?.layoutPosition === 'Left' && (
        <div className={isSideBySide ? styles.FeatureCardWideGroup : undefined}>
          <FeatureCard
            card={item.featureCard}
            wide={isWide}
            sideBySide={isSideBySide}
            menuPathPrefix={item.label}
          />
          {isSideBySide &&
            ((item.featureCard.links?.length ?? 0) > 0 ||
              (item.featureCard.secondaryLinks?.length ?? 0) > 0) && (
              <div className={styles.FeaturedLinksPanel}>
                {item.featureCard.links &&
                  item.featureCard.links.length > 0 && (
                    <>
                      <span className={styles.FeaturedLinksPanelTitle}>
                        Featured
                      </span>
                      <div className={styles.FeaturedLinksPanelList}>
                        {item.featureCard.links.map((link) => (
                          <a
                            key={`${link.label}-${link.url}`}
                            href={link.url}
                            className={styles.FeaturedLinksPanelLink}
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </>
                  )}
                {item.featureCard.secondaryLinks &&
                  item.featureCard.secondaryLinks.length > 0 && (
                    <div
                      className={cn(
                        styles.FeaturedLinksPanelList,
                        styles.FeaturedLinksPanelListBottom
                      )}
                    >
                      {item.featureCard.secondaryLinks.map((link) => (
                        <a
                          key={`${link.label}-${link.url}`}
                          href={link.url}
                          className={styles.FeaturedLinksPanelLink}
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
              </div>
            )}
        </div>
      )}
      <div className={styles.ContentContainer}>
        {item.title && item.title.label && (
          <div className={styles.Title}>
            {item.title.url ? (
              <a
                href={item.title.url}
                className={styles.MainTitleLink}
                onClick={(e) => {
                  const navProps = {
                    level: MegaMenuLevels.level_2,
                    linkUrl: item.title!.url,
                    linkText: item.title!.label,
                    menuPath: `${item.label}${MENU_PATH_DELIMITER}${
                      item.title!.label
                    }`,
                  };
                  if (isModifiedClick(e)) {
                    trackMegaMenuNavigation(navProps);
                    return;
                  }
                  e.preventDefault();
                  const { url } = item.title!;
                  trackMegaMenuNavigation({
                    ...navProps,
                    eventCallback: () => {
                      window.location.href = url;
                    },
                  });
                }}
              >
                {item.title.label}
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.75 12H20.25"
                    stroke="#161716"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13.5 5.25L20.25 12L13.5 18.75"
                    stroke="#161716"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
            ) : (
              <span className={styles.MainTitleLink}>{item.title.label}</span>
            )}
          </div>
        )}
        <div className={styles.Divider} />
        {item.topBrandIcons && item.topBrandIcons.length > 0 && (
          <>
            <div className={styles.TopBrandIcons}>
              {item.topBrandIconsTitle && (
                <span className={styles.TopBrandIconsTitle}>
                  {item.topBrandIconsTitle}
                </span>
              )}
              <div className={styles.TopBrandIconsGrid}>
                {item.topBrandIcons.map((icon) => (
                  <a
                    key={icon.url}
                    href={icon.url}
                    className={styles.TopBrandIconItem}
                    onClick={(e) => {
                      const navProps = {
                        level: MegaMenuLevels.level_4,
                        linkUrl: icon.url,
                        linkText: null,
                        menuPath: `${item.label}${MENU_PATH_DELIMITER}${
                          icon.alt ?? ''
                        }`,
                      };
                      if (isModifiedClick(e)) {
                        trackMegaMenuNavigation(navProps);
                        return;
                      }
                      e.preventDefault();
                      trackMegaMenuNavigation({
                        ...navProps,
                        eventCallback: () => {
                          window.location.href = icon.url;
                        },
                      });
                    }}
                  >
                    <Image
                      src={icon.logo}
                      alt={icon.alt ?? ''}
                      width={128}
                      height={64}
                      className={styles.TopBrandIconImage}
                    />
                  </a>
                ))}
              </div>
            </div>
            <div className={styles.Divider} />
          </>
        )}
        <LinkColumns
          columns={item.linkColumns}
          sideBySide={isSideBySide}
          menuPathPrefix={menuPathPrefix}
        />
      </div>
      {item.featureCard && item.featureCard.layoutPosition !== 'Left' && (
        <div className={isSideBySide ? styles.FeatureCardWideGroup : undefined}>
          {isSideBySide &&
            ((item.featureCard.links?.length ?? 0) > 0 ||
              (item.featureCard.secondaryLinks?.length ?? 0) > 0) && (
              <div
                className={cn(
                  styles.FeaturedLinksPanel,
                  styles.FeaturedLinksPanelRight
                )}
              >
                {item.featureCard.links &&
                  item.featureCard.links.length > 0 && (
                    <>
                      <span className={styles.FeaturedLinksPanelTitle}>
                        Featured
                      </span>
                      <div className={styles.FeaturedLinksPanelList}>
                        {item.featureCard.links.map((link) => (
                          <a
                            key={`${link.label}-${link.url}`}
                            href={link.url}
                            className={styles.FeaturedLinksPanelLink}
                          >
                            {link.label}
                          </a>
                        ))}
                      </div>
                    </>
                  )}
                {item.featureCard.secondaryLinks &&
                  item.featureCard.secondaryLinks.length > 0 && (
                    <div
                      className={cn(
                        styles.FeaturedLinksPanelList,
                        styles.FeaturedLinksPanelListBottom
                      )}
                    >
                      {item.featureCard.secondaryLinks.map((link) => (
                        <a
                          key={`${link.label}-${link.url}`}
                          href={link.url}
                          className={styles.FeaturedLinksPanelLink}
                        >
                          {link.label}
                        </a>
                      ))}
                    </div>
                  )}
              </div>
            )}
          <FeatureCard
            card={item.featureCard}
            wide={isWide}
            sideBySide={isSideBySide}
            menuPathPrefix={item.label}
          />
        </div>
      )}
    </>
  );
}
